import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import StaticMapImage from './maps/StaticMapImage';
import { useMapProvider } from './maps/MapProvider';
import { UniversalMapEntry } from './maps/MapInterface';

/**
 * StaticMapTest Component
 * 
 * Test component for validating Phase 3 Google Maps integration
 */
const StaticMapTest: React.FC = () => {
  const { provider, setProvider } = useMapProvider();
  
  // Sample test data
  const testEntries: UniversalMapEntry[] = [
    {
      id: 1,
      title: 'Disney World',
      location: { lat: 28.3852, lng: -81.5639 },
      date: '2025-01-01',
      description: 'Magic Kingdom visit',
      privacy: 'public'
    },
    {
      id: 2,
      title: 'Universal Studios',
      location: { lat: 28.4743, lng: -81.4685 },
      date: '2025-01-02',
      description: 'Theme park adventure',
      privacy: 'public'
    }
  ];

  const [showMarkers, setShowMarkers] = useState(true);
  const [mapWidth, setMapWidth] = useState(800);
  const [mapHeight, setMapHeight] = useState(400);
  const [zoom, setZoom] = useState(10);
  const [mapType, setMapType] = useState<'roadmap' | 'satellite' | 'hybrid' | 'terrain'>('roadmap');

  const handleProviderChange = (newProvider: 'google' | 'mapbox') => {
    setProvider(newProvider);
  };

  const handleError = (error: Error) => {
    console.error('Static map error:', error);
  };

  const handleLoad = () => {
    console.log('Static map loaded successfully');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Phase 3: Static Map Provider Testing
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Provider: {provider}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button 
              variant={provider === 'mapbox' ? 'contained' : 'outlined'}
              onClick={() => handleProviderChange('mapbox')}
            >
              Mapbox
            </Button>
            <Button 
              variant={provider === 'google' ? 'contained' : 'outlined'}
              onClick={() => handleProviderChange('google')}
            >
              Google Maps
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Width</InputLabel>
              <Select
                value={mapWidth}
                label="Width"
                onChange={(e) => setMapWidth(Number(e.target.value))}
              >
                <MenuItem value={600}>600px</MenuItem>
                <MenuItem value={800}>800px</MenuItem>
                <MenuItem value={1200}>1200px</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Height</InputLabel>
              <Select
                value={mapHeight}
                label="Height"
                onChange={(e) => setMapHeight(Number(e.target.value))}
              >
                <MenuItem value={300}>300px</MenuItem>
                <MenuItem value={400}>400px</MenuItem>
                <MenuItem value={600}>600px</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Zoom</InputLabel>
              <Select
                value={zoom}
                label="Zoom"
                onChange={(e) => setZoom(Number(e.target.value))}
              >
                <MenuItem value={8}>8</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value={14}>14</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Map Type</InputLabel>
              <Select
                value={mapType}
                label="Map Type"
                onChange={(e) => setMapType(e.target.value as any)}
              >
                <MenuItem value="roadmap">Roadmap</MenuItem>
                <MenuItem value="satellite">Satellite</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
                <MenuItem value="terrain">Terrain</MenuItem>
              </Select>
            </FormControl>

            <Button 
              variant={showMarkers ? 'contained' : 'outlined'}
              onClick={() => setShowMarkers(!showMarkers)}
            >
              {showMarkers ? 'Hide' : 'Show'} Markers
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Static Map ({provider}) - {mapWidth}x{mapHeight}
          </Typography>
          
          <Box sx={{ 
            border: '2px solid #e0e0e0', 
            borderRadius: 1, 
            overflow: 'hidden',
            display: 'inline-block'
          }}>
            <StaticMapImage
              entries={testEntries}
              width={mapWidth}
              height={mapHeight}
              zoom={zoom}
              showMarkers={showMarkers}
              mapType={mapType}
              onError={handleError}
              onLoad={handleLoad}
              alt={`Static map powered by ${provider}`}
            />
          </Box>
          
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Testing with {testEntries.length} entries • Provider: {provider} • 
            Type: {mapType} • Markers: {showMarkers ? 'Enabled' : 'Disabled'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StaticMapTest;